import {
  FeedbackMessage,
  InputNumberInteger,
  InputSelectAsync,
} from "@components-common/forms";
import { useField } from "formik";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "taltech-styleguide";
import { LayoutRow, Room } from "./building-layout.types";
import { hasNoRooms } from "./predicates";

/**
 * RowGenerationDefinition Component
 *
 * Renders input fields for defining the number of rooms and their widths for a specific row.
 *
 * @param {Object} props - Component properties
 * @param {string} props.label - The label for the row
 * @param {string} props.id - The unique identifier for the row
 * @returns {JSX.Element} - The JSX code for row generation definition
 */
const RowGenerationDefinition = ({
  label,
  id,
}: {
  label: string;
  id: string;
}) => {
  return (
    <div id={id} className="">
      <h6 className="mb-0">{label}</h6>
      <div className={"d-flex justify-content-between"}>
        <div>
          <InputNumberInteger
            id={`${id}.numberOfRooms`}
            label={"No. rooms"}
            elementClass="required"
          />
        </div>
        <div className={"ms-3"}>
          <InputNumberInteger
            id={`${id}.width`}
            label={"Width"}
            suffix=" m"
            elementClass="required"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * RowsInformation Component
 *
 * Manages the form state for two rows of a building layout and handles user interactions.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.spaceFuncOptions - Options for the space functions select input
 * @returns {JSX.Element} - The JSX code for rows information
 */
export const RowsInformation = (props: any) => {
  const { spaceFuncOptions } = props;
  const [fieldRowFirst, metaRowFirst, helpersRowFirst] =
    useField<LayoutRow>(`rowFirst`);
  const [fieldRowSecond, metaRowSecond, helpersRowSecond] =
    useField(`rowSecond`);
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);

  useEffect(() => {
    const rowFirst = fieldRowFirst.value;
    const rowSecond = fieldRowSecond.value;

    const rowFirstChanged =
      rowFirst.rooms.filter((x: Room) => x.width != rowFirst.width).length >
        0 || rowFirst.rooms.length != rowFirst.numberOfRooms;
    const rowSecondChanged =
      rowSecond.rooms.filter((x: Room) => x.width != rowSecond.width).length >
        0 || rowSecond.rooms.length != rowSecond.numberOfRooms;

    if (hasNoRooms(fieldRowFirst) && hasNoRooms(fieldRowSecond)) {
      setValuesChanged(false);
    } else {
      setValuesChanged(rowFirstChanged || rowSecondChanged);
    }
  }, [fieldRowFirst, fieldRowSecond]);

  return (
    <>
      <Row>
        <Form.Group className="space-functions-select mb-1">
          <InputSelectAsync
            id="spaceFunctions"
            label="Building space (Functions)"
            options={spaceFuncOptions}
            source={{ type: "" }}
          />
        </Form.Group>
        {spaceFuncOptions.length === 0 && (
          <span className="text-danger">
            You must have at least 1 space function to be able to generate
            rooms.
          </span>
        )}
      </Row>
      <Row>
        <Col
          sm={12}
          md={9}
          lg={6}
          className={"d-flex justify-content-between mt-3"}
        >
          <RowGenerationDefinition label={"Row 1"} id={"rowFirst"} />
          <RowGenerationDefinition label={"Row 2"} id={"rowSecond"} />
        </Col>
      </Row>
      <Row className="mb-3">
        {valuesChanged && (
          <div className="text-danger mt-1">
            Values have been changed. Please generate rooms again.
          </div>
        )}
      </Row>
    </>
  );
};
